import React, { useState, useEffect, useRef } from 'react';

function AddressAutocomplete({ 
  value, 
  onChange, 
  onPlaceSelect,
  placeholder = "Enter your address",
  className = "",
  disabled = false
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiStatus, setApiStatus] = useState('checking');
  const inputRef = useRef(null);
  const wrapperRef = useRef(null);
  const autocompleteService = useRef(null);
  const placesService = useRef(null);

  // Initialize Google Places services
  useEffect(() => {
    const initializeServices = async () => {
      if (!process.env.REACT_APP_GOOGLE_MAPS_API_KEY) {
        console.warn('Google Maps API key not found');
        setApiStatus('no-key');
        return;
      }

      try {
        // Load Google Maps API if needed
        if (!window.google?.maps?.places) {
          console.log('Loading Google Maps API...');
          await loadGoogleMapsScript();
        }

        // Double-check that the API loaded successfully
        if (window.google?.maps?.places) {
          console.log('Initializing Google Places services...');
          autocompleteService.current = new window.google.maps.places.AutocompleteService();
          // Create a hidden div for PlacesService (it requires a map or element)
          const hiddenDiv = document.createElement('div');
          placesService.current = new window.google.maps.places.PlacesService(hiddenDiv);
          setApiStatus('ready');
          console.log('Google Places services ready');
        } else {
          console.error('Google Maps API failed to load properly');
          setApiStatus('error');
        }
      } catch (error) {
        console.error('Google Maps API initialization error:', error);
        setApiStatus('error');
      }
    };

    initializeServices();
  }, []);

  // Handle input changes
  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    
    // Create proper event for parent component
    const syntheticEvent = {
      target: {
        name: 'location.address',
        value: inputValue,
        type: 'text'
      }
    };

    if (onChange) {
      onChange(syntheticEvent);
    }

    // Fetch suggestions if we have Google Places API
    if (autocompleteService.current && inputValue.length > 2) {
      fetchSuggestions(inputValue);
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Fetch address suggestions
  const fetchSuggestions = (input) => {
    if (!autocompleteService.current) return;

    setIsLoading(true);
    
    autocompleteService.current.getPlacePredictions(
      {
        input: input,
        types: ['address'],
        componentRestrictions: { country: 'us' }
      },
      (predictions, status) => {
        setIsLoading(false);
        
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          setSuggestions(predictions.slice(0, 5)); // Limit to 5 suggestions
          setShowSuggestions(true);
        } else {
          setSuggestions([]);
          setShowSuggestions(false);
        }
      }
    );
  };

  // Handle suggestion selection
  const handleSuggestionClick = (suggestion) => {
    if (!placesService.current) return;

    // Get detailed place information
    placesService.current.getDetails(
      {
        placeId: suggestion.place_id,
        fields: ['address_components', 'formatted_address', 'geometry']
      },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          // Parse address components
          const addressComponents = {
            streetNumber: '',
            streetName: '',
            city: '',
            state: '',
            zipCode: ''
          };
          
          if (place.address_components) {
            place.address_components.forEach(component => {
              const types = component.types;
              if (types.includes('street_number')) {
                addressComponents.streetNumber = component.long_name;
              }
              if (types.includes('route')) {
                addressComponents.streetName = component.long_name;
              }
              if (types.includes('locality')) {
                addressComponents.city = component.long_name;
              }
              if (types.includes('administrative_area_level_1')) {
                addressComponents.state = component.short_name;
              }
              if (types.includes('postal_code')) {
                addressComponents.zipCode = component.long_name;
              }
            });
          }

          // Build just the street address (number + street name)
          const streetAddress = [
            addressComponents.streetNumber,
            addressComponents.streetName
          ].filter(Boolean).join(' ');

          // Use street address only, or fallback to first part of formatted address
          let addressFieldValue = streetAddress;
          if (!addressFieldValue && place.formatted_address) {
            // Fallback: try to extract street address from formatted address
            // Split by comma and take the first part (usually the street address)
            const addressParts = place.formatted_address.split(',');
            addressFieldValue = addressParts[0]?.trim() || place.formatted_address;
          }

          // Update the input value with just the street address
          const syntheticEvent = {
            target: {
              name: 'location.address',
              value: addressFieldValue,
              type: 'text'
            }
          };
          
          if (onChange) {
            onChange(syntheticEvent);
          }

          // Call onPlaceSelect to update other fields with the parsed components
          if (onPlaceSelect) {
            onPlaceSelect({
              address: addressFieldValue,
              city: addressComponents.city || '',
              state: addressComponents.state || '',
              zipCode: addressComponents.zipCode || '',
              coordinates: place.geometry?.location ? [
                place.geometry.location.lng(),
                place.geometry.location.lat()
              ] : null
            });
          }
        }
        
        // Hide suggestions
        setSuggestions([]);
        setShowSuggestions(false);
      }
    );
  };

  // Handle clicking outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Load Google Maps script
  const loadGoogleMapsScript = () => {
    return new Promise((resolve, reject) => {
      // Check if script is already loaded
      if (window.google?.maps?.places) {
        console.log('Google Maps API already loaded');
        resolve();
        return;
      }

      // Check if script is already being loaded
      const existingScript = document.querySelector('script[src*="maps.googleapis.com"]');
      if (existingScript) {
        console.log('Google Maps script already exists, waiting for load...');
        // Wait for the existing script to load
        const checkGoogle = setInterval(() => {
          if (window.google?.maps?.places) {
            console.log('Google Maps API loaded via existing script');
            clearInterval(checkGoogle);
            resolve();
          }
        }, 100);
        
        // Timeout after 10 seconds
        setTimeout(() => {
          clearInterval(checkGoogle);
          if (!window.google?.maps?.places) {
            console.error('Google Maps API loading timeout');
            reject(new Error('Google Maps API loading timeout'));
          }
        }, 10000);
        return;
      }

      console.log('Creating new Google Maps script...');
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&libraries=places&loading=async`;
      script.async = true;
      script.defer = true;
      
      script.onload = () => {
        console.log('Google Maps script loaded successfully');
        // Sometimes the API takes a moment to initialize after script load
        const checkReady = setInterval(() => {
          if (window.google?.maps?.places) {
            clearInterval(checkReady);
            resolve();
          }
        }, 50);
        
        // Timeout after 5 seconds
        setTimeout(() => {
          clearInterval(checkReady);
          if (!window.google?.maps?.places) {
            reject(new Error('Google Maps API not ready after script load'));
          }
        }, 5000);
      };
      
      script.onerror = (error) => {
        console.error('Failed to load Google Maps script:', error);
        reject(error);
      };
      
      document.head.appendChild(script);
    });
  };

  const getStatusIcon = () => {
    if (isLoading) {
      return <div className="w-4 h-4 border-2 border-[#9dacff] border-t-transparent rounded-full animate-spin"></div>;
    }
    
    switch (apiStatus) {
      case 'ready':
        return <svg className="w-4 h-4 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>;
      case 'error':
        return <svg className="w-4 h-4 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>;
      default:
        return <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>;
    }
  };

  const getStatusMessage = () => {
    if (isLoading) return 'Searching addresses...';
    
    switch (apiStatus) {
      case 'ready':
        return ''; // No message when ready
      case 'no-key':
        return 'Google Maps API key not configured';
      case 'error':
        return 'Failed to load address suggestions';
      default:
        return 'Enter your address manually';
    }
  };

  return (
    <div ref={wrapperRef} className="relative">
      <input
        ref={inputRef}
        type="text"
        value={value || ''}
        onChange={handleInputChange}
        placeholder={placeholder}
        disabled={disabled}
        className={`w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:border-[#9dacff] ${className}`}
        autoComplete="off"
      />
      
      {/* Status indicator */}
      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
        {getStatusIcon()}
      </div>

      {/* Suggestions dropdown */}
      {showSuggestions && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion.place_id}
              className="px-4 py-3 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
              onClick={() => handleSuggestionClick(suggestion)}
            >
              <div className="text-sm text-[#254e70] font-medium">
                {suggestion.structured_formatting.main_text}
              </div>
              <div className="text-xs text-gray-600">
                {suggestion.structured_formatting.secondary_text}
              </div>
            </div>
          ))}
        </div>
      )}
      
      {/* Status message */}
      {getStatusMessage() && (
        <div className={`text-xs mt-1 ${apiStatus === 'ready' ? 'text-green-600' : apiStatus === 'error' ? 'text-red-600' : 'text-[#254e70]/60'}`}>
          {getStatusMessage()}
        </div>
      )}
    </div>
  );
}

export default AddressAutocomplete; 